import { useQuery } from "@rocicorp/zero/react";
import { styled } from "goober";
import { STORAGE_USAGE_REFRESH_INTERVAL_MS } from "../../../shared/monitoringWorkflowsReport";
import { queries } from "../../../shared/queries";
import { useFormatRelativeTime } from "../../hooks/useFormatRelativeTime";
import { useAccessCheck } from "../../hooks/useHasAccess";
import { formatBytes } from "../../lib/formatBytes";
import { formatHoverTime } from "../../lib/formatTimestamp";
import { navigate, routes, useCurrentUrl, useMakeUrl } from "../../lib/urls";
import { Button } from "../Button";
import { ButtonPress } from "../Button/ButtonPress";
import { CountBadge } from "../CountBadge";
import { Dropdown } from "../Dropdown";
import { HelpTooltip } from "../HelpTooltip";
import { Icon } from "../Icon";
import { Link } from "../Link";
import { Modal } from "../Modal";
import { SubtleButton } from "../SubtleButton";
import { Text } from "../Text";
import {
  DETAIL_MODAL_STYLE,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeaderRight,
  ModalTitle,
} from "./MonitoringDetailCard";
import { type StorageHeaderParts, StorageFiles } from "./StorageFiles";
import { StorageCard, StorageCardHeader, StorageTiles } from "./StorageTiles";
import {
  parseStoragePath,
  storagePath,
  type StorageUsageLevel,
  storageUsageLevel,
  type StorageVolumeTarget,
} from "./storageUsageLevel";
import { useMonitoringWorkflows } from "./useMonitoringWorkflows";
import { useStorageUsage } from "./useStorageUsage";
import { PanelSkeleton } from "./workflowsLayout";

const Nav = styled("div")`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const IndexLink = styled(Link)`
  display: flex;
  flex-shrink: 0;
  color: var(--text-secondary);
  text-decoration: none;

  &:hover {
    color: var(--text-primary);
  }
`;

const Separator = () => (
  <Icon name="slash" size={12} color="var(--border-alpha)" />
);

// Sits where the back button does below the top level, so one swaps for the other.
const TopIcon = styled("span")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`;

const HeaderEnd = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const Freshness = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
`;

// The icon set only has vertical dots.
const HorizontalDots = styled("span")`
  display: inline-flex;
  margin: 0 -4px;
  transform: rotate(90deg);
`;

export function StorageUsageModal({ onClose }: { onClose: () => void }) {
  return (
    <Modal
      open
      onClose={onClose}
      aria-label="Storage usage"
      style={DETAIL_MODAL_STYLE}
    >
      <ModalHeader>
        <ModalTitle icon="box" title="Storage usage" />
        <ModalHeaderRight>
          <Button size="l" variant="secondary" icon="x" onClick={onClose} />
        </ModalHeaderRight>
      </ModalHeader>
      <StorageUsageBody onClose={onClose} />
    </Modal>
  );
}

function StorageUsageBody({ onClose }: { onClose: () => void }) {
  const { loading, rows, spaceTile } = useStorageUsage({ pollStorage: true });
  const { path, dir } = useCurrentUrl(routes.modal.storageUsage);
  const [personalSpace] = useQuery(queries.personalSpace());
  const keys = parseStoragePath(path);
  const hasAccess = useAccessCheck();

  const done = (
    <Button size="l" variant="primary" onClick={onClose}>
      Done
    </Button>
  );

  if (loading) {
    return (
      <>
        <ModalBody>
          <PanelSkeleton />
        </ModalBody>
        <ModalFooter>
          <StorageFreshness />
          {done}
        </ModalFooter>
      </>
    );
  }

  const level = storageUsageLevel(rows, keys, spaceTile);
  const applied = level.crumbs.at(-1)?.keys ?? [];
  const drillPath = (key: string) => storagePath([...applied, key]);
  const canDelete = (target: StorageVolumeTarget) => {
    switch (target.category) {
      case "personal_volume":
        return target.spaceId === personalSpace?.id;
      case "live_volume":
        return hasAccess("space:push_live", target.spaceId);
      default:
        return hasAccess("space:write", target.spaceId);
    }
  };

  const headerWith = ({
    count,
    showTotal = true,
    action,
  }: StorageHeaderParts = {}) => (
    <StorageCardHeader>
      <StorageNav
        crumbs={level.crumbs}
        dirs={level.view === "files" && dir ? dir.split("/") : []}
        count={count}
      />
      <HeaderEnd>
        {showTotal && (
          <Text variant="text-small-md">{formatBytes(level.totalBytes)}</Text>
        )}
        {action}
      </HeaderEnd>
    </StorageCardHeader>
  );

  return (
    <>
      {level.view === "files" && level.files ? (
        <ModalBody>
          <StorageCard>
            <StorageFiles
              target={level.files}
              canDelete={canDelete(level.files)}
              header={headerWith}
            />
          </StorageCard>
        </ModalBody>
      ) : (
        <StorageTiles
          header={headerWith()}
          level={level}
          drillPath={drillPath}
          canDelete={canDelete}
          canEditRetention={(spaceId) => hasAccess("space:write", spaceId)}
        />
      )}
      <ModalFooter>
        <StorageFreshness />
        {done}
      </ModalFooter>
    </>
  );
}

function StorageFreshness() {
  const { report } = useMonitoringWorkflows();
  const formatRelativeTime = useFormatRelativeTime();
  const minutes = STORAGE_USAGE_REFRESH_INTERVAL_MS / 60_000;
  const sampledAt = report.storageSampledAt;
  return (
    <Freshness>
      <Text variant="text-small" color="var(--text-secondary)">
        {sampledAt === null
          ? "Not measured yet"
          : `Updated ${formatRelativeTime(sampledAt)}`}
      </Text>
      <HelpTooltip
        content={
          sampledAt === null
            ? `Storage is measured every ${minutes} minutes.`
            : `Storage is measured every ${minutes} minutes. Last measured at ${formatHoverTime(sampledAt)}.`
        }
      />
    </Freshness>
  );
}

// A back button to the level above, then the top level and, from two levels
// down, a menu of the levels between it and this one.
function StorageNav({
  crumbs,
  dirs,
  count,
}: {
  crumbs: StorageUsageLevel["crumbs"];
  dirs: string[];
  count?: number;
}) {
  const makeUrl = useMakeUrl();
  if (crumbs.length === 0) {
    return (
      <Nav>
        <TopIcon>
          <Icon name="list-tree" size={12} color="var(--text-primary)" />
        </TopIcon>
        <Text variant="text-small-md">Storage</Text>
      </Nav>
    );
  }
  const levelPath = storagePath(crumbs.at(-1)!.keys);
  const trail = [
    { key: "", label: "Storage", params: { path: "", dir: "" } },
    ...crumbs.map((crumb) => {
      const path = storagePath(crumb.keys);
      return { key: path, label: crumb.label, params: { path, dir: "" } };
    }),
    ...dirs.map((label, i) => {
      const dir = dirs.slice(0, i + 1).join("/");
      return { key: `dir:${dir}`, label, params: { path: levelPath, dir } };
    }),
  ];
  const [root, ...rest] = trail;
  const current = trail.at(-1)!;
  const between = rest.slice(0, -1);
  const parent = trail.at(-2)!;
  const urlOf = (params: { path: string; dir: string }) =>
    makeUrl(routes.modal.storageUsage, params);
  return (
    <Nav>
      <ButtonPress
        variant="secondary"
        icon="arrow-left"
        aria-label={`Back to ${parent.label}`}
        onPress={() => navigate(routes.modal.storageUsage, parent.params)}
      />
      <IndexLink to={urlOf(root!.params)}>
        <Text variant="text-small-md" color="inherit">
          {root!.label}
        </Text>
      </IndexLink>
      <Separator />
      {between.length > 0 && (
        <>
          <Dropdown
            align="bottom-left"
            minWidth={180}
            opener={
              <HorizontalDots>
                <SubtleButton
                  icon="more"
                  variant="secondary"
                  aria-label="Go to a level above"
                />
              </HorizontalDots>
            }
            sections={[
              {
                items: between.map((level) => ({
                  key: level.key,
                  type: "link" as const,
                  left: null,
                  label: level.label,
                  to: urlOf(level.params),
                })),
              },
            ]}
          />
          <Separator />
        </>
      )}
      <Text variant="text-small-md" ellipsis>
        {current.label}
      </Text>
      {count !== undefined && (
        <CountBadge>
          <Text variant="text-smaller-md">{count}</Text>
        </CountBadge>
      )}
    </Nav>
  );
}
